// The four standard arena sizes, shared by the server (public boxes, custom
// lobbies) and the client (Play locally, Create lobby). A size is an AREA: the
// side of the square with that area, the same for every shape, so a Medium
// Stadium has exactly as much room as a Medium Rectangle. Tuned by playing on a
// phone: Small for a 1v1, Medium for 4-5 players, Large for 8-12, Huge for about
// 25 (a computer's screen).
//
// Shapes: "rectangle" and "stadium" are the standard 2:1 arenas (lying along x;
// see game-state-functions equalAreaRectangle / equalAreaStadium); "square" and
// "circle" are the classic ones, still offered when you set up a game yourself.
import * as gsf from "./game-state-functions.js";

// Each size is its area; `side` is the side of the square with that area.
export var ARENA_SIZES = [
    { key: "small", label: "Small", area: 200000 },
    { key: "medium", label: "Medium", area: 400000 },
    { key: "large", label: "Large", area: 800000 },
    { key: "huge", label: "Huge", area: 1600000 }
].map(function (s) {
    return { key: s.key, label: s.label, area: s.area, side: Math.sqrt(s.area) };
});

export var ARENA_SHAPES = ["rectangle", "stadium", "square", "circle"];

// The side of a size key's square ("medium" -> 632.5...), Small for an unknown key.
export function sideOfKey(sizeKey) {
    for (var i = 0; i < ARENA_SIZES.length; i++) {
        if (ARENA_SIZES[i].key === sizeKey) { return ARENA_SIZES[i].side; }
    }
    return ARENA_SIZES[0].side;
}

// The size for a room of `players`: Small for a 1v1, Medium up to 5, Large up
// to 12, Huge beyond (about 25 is its sweet spot).
export function sizeKeyForPlayers(players) {
    if (players <= 2) { return "small"; }
    if (players <= 5) { return "medium"; }
    if (players <= 12) { return "large"; }
    return "huge";
}

// The diameter of the circle with the area of a side x side square.
function equalAreaDiameter(side) {
    return 2 * side / Math.sqrt(Math.PI);
}

// The map value string ("Rectangle 877 438", "Circle 700", ...) for a shape at a
// standard size key, or at any `side` (a number) with the same equal-area rule.
export function arenaValue(shape, sizeKeyOrSide) {
    var side = typeof sizeKeyOrSide === "number" ? sizeKeyOrSide : sideOfKey(sizeKeyOrSide);
    if (shape === "stadium") {
        var st = gsf.equalAreaStadium(equalAreaDiameter(side));
        return "Stadium " + st.width + " " + st.height;
    }
    if (shape === "square") {
        return "Square " + Math.round(side);
    }
    if (shape === "circle") {
        return "Circle " + Math.round(equalAreaDiameter(side));
    }
    var r = gsf.equalAreaRectangle(side);
    return "Rectangle " + r.width + " " + r.height;
}

// A map value's shape and size (the side of the square with its area), or null.
export function arenaOf(value) {
    var parts = String(value).split(" ");
    var a = Number(parts[1]), b = Number(parts[2]);
    if (!(a > 0)) { return null; }
    if (parts[0] === "Square") { return { shape: "square", side: a }; }
    if (parts[0] === "Circle") { return { shape: "circle", side: Math.sqrt(Math.PI) * a / 2 }; }
    if (!(b > 0)) { return null; }
    if (parts[0] === "Rectangle") { return { shape: "rectangle", side: Math.sqrt(a * b) }; }
    if (parts[0] === "Stadium") { return { shape: "stadium", side: Math.sqrt(Math.PI) * gsf.equivalentCircleDiameter(a, b) / 2 }; }
    return null;
}

// The standard size nearest to a side (by ratio: 1200 is Large, not Medium).
export function nearestSizeKey(side) {
    var best = ARENA_SIZES[0], bestD = Infinity;
    ARENA_SIZES.forEach(function (s) {
        var d = Math.abs(Math.log(side / s.side));
        if (d < bestD) { bestD = d; best = s; }
    });
    return best.key;
}

// Any map value snapped to its shape at the nearest standard size (an old
// "Square 700" -> "Square 632", Medium). Unknown values come back unchanged.
export function normalizeArenaValue(value) {
    var a = arenaOf(value);
    return a ? arenaValue(a.shape, nearestSizeKey(a.side)) : value;
}
